import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { uploadService } from '../services/uploadService';
import './Profile.css';

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'data' | 'password'>('data');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    logo_url: '',
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isClinic = user?.role === 'clinica';

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        logo_url: user.logo_url || '',
      });
      setLogoPreview(user.logo_url ? uploadService.getImageUrl(user.logo_url) : null);
    }
  }, [user]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setSaving(true);
    try {
      let logoUrl: string | undefined = formData.logo_url || undefined;
      if (logoFile) {
        const uploadResult = await uploadService.uploadImage(logoFile);
        logoUrl = uploadResult.image_url;
      }
      await authService.updateProfile({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        logo_url: logoUrl,
      });
      await refreshUser();
      setLogoFile(null);
      setMessage({ type: 'success', text: 'Dados atualizados com sucesso.' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Erro ao atualizar dados. Tente novamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'A nova senha e a confirmação não coincidem.' });
      return;
    }
    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'A nova senha deve ter no mínimo 6 caracteres.' });
      return;
    }
    setSaving(true);
    try {
      await authService.changePassword(passwordData.current_password, passwordData.new_password);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setMessage({ type: 'success', text: 'Senha alterada com sucesso.' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Erro ao alterar senha. Verifique a senha atual.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const canChangePassword = !!user.email && user.role !== 'comum'; // usuários com login por senha (clínica, admin)

  return (
    <div className="container profile-page">
      <div className="page-header">
        <h1>Meu cadastro</h1>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
          {message.text}
        </div>
      )}

      <div className="profile-tabs">
        <button
          type="button"
          className={`profile-tab ${activeTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          Dados cadastrais
        </button>
        {canChangePassword && (
          <button
            type="button"
            className={`profile-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            Trocar senha
          </button>
        )}
      </div>

      {activeTab === 'data' && (
        <div className="card profile-card">
          <form onSubmit={handleSaveProfile}>
            {isClinic && (
              <div className="form-group profile-logo-group">
                <label>Logo da clínica</label>
                <div className="profile-logo-row">
                  <div className="profile-logo-preview">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" />
                    ) : (
                      <span className="profile-logo-placeholder">Sem logo</span>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Nome / Razão social</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>E-mail</label>
              <input
                type="email"
                className="form-control"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Telefone</label>
              <input
                type="text"
                className="form-control"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(00) 00000-0000"
              />
            </div>
            {isClinic && user.cnpj && (
              <div className="form-group">
                <label>CNPJ</label>
                <input
                  type="text"
                  className="form-control"
                  value={user.cnpj?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') || user.cnpj}
                  disabled
                  readOnly
                />
                <small className="form-text text-muted">CNPJ não pode ser alterado. Contate o administrador se necessário.</small>
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'password' && canChangePassword && (
        <div className="card profile-card">
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label>Senha atual</label>
              <input
                type="password"
                className="form-control"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="form-group">
              <label>Nova senha</label>
              <input
                type="password"
                className="form-control"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                autoComplete="new-password"
              />
              <small className="form-text text-muted">Mínimo de 6 caracteres.</small>
            </div>
            <div className="form-group">
              <label>Confirmar nova senha</label>
              <input
                type="password"
                className="form-control"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                autoComplete="new-password"
              />
            </div>
            <p className="profile-recover-hint">
              Esqueceu sua senha? Entre em contato com o administrador do sistema para redefinição.
            </p>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Alterando...' : 'Trocar senha'}
              </button>
            </div>
          </form>
        </div>
      )}

      {!canChangePassword && user.role === 'comum' && (
        <div className="card profile-card">
          <p className="text-muted">
            Usuários com login Gov.br não alteram senha por aqui. Para atualizar nome, e-mail ou telefone, use o cadastro no Gov.br.
          </p>
        </div>
      )}
    </div>
  );
};

export default Profile;
